import React, { memo } from 'react';
import Truncate from 'react-truncate';
import styled, { css } from 'styled-components';

import usePaths from 'hooks/use-paths';
import useUI from 'hooks/use-ui';
import { Link } from 'components/core';
import { tracker } from 'utils/analytics';
import { Product } from 'types/graphql-overrides';
import { getCategoryForProduct } from 'shared/helpers/products';

import { Price } from './price';
import { StrainDetails } from './strain-details';
import { QuickAddButton } from './quick-add-button';
import { Brand, Container, Content, Details, Name, ProductImage } from './product-card.components';
import { ProductCardProduct } from './product-card.types';
import { getBrandName } from './product-card.utils';

export const TEST_ID_LIST_VIEW_PRODUCT_CARD = 'list-view-product-card-anchor';

export type ListViewProductCardProps = {
  product: ProductCardProduct;
  productIndex: number;
  onClick?: () => void;
  onQuickAddClick?: () => void;
  href?: string;
  route?: string;
};

type UseProductCardPathParams = Pick<ListViewProductCardProps, 'href' | 'product'>;

type UseProductCardPathReturn = {
  href: string;
  route: string | null;
};

function useProductCardPath({ product, href }: UseProductCardPathParams): UseProductCardPathReturn {
  const paths = usePaths({
    category: getCategoryForProduct(product),
    product: { id: product.id },
  });

  return {
    href: href ?? paths.href,
    route: href ? null : paths.href,
  };
}

export const ListViewProductCard = memo(
  ({
    product,
    productIndex,
    onQuickAddClick,
    onClick,
    href: hrefOverride,
  }: ListViewProductCardProps): JSX.Element | null => {
    const UI = useUI();
    const { href, route } = useProductCardPath({ product, href: hrefOverride });

    const brandName = getBrandName(product);

    const handleClick = (): void => {
      onClick?.();
      const analyticsProduct = product as Product;
      UI.setProductPosition({ productId: analyticsProduct.id, productIndex });
      tracker.setContext({ activeProductPosition: { index: productIndex } });
      tracker.productClicked({ product: analyticsProduct, position: productIndex });
    };

    return (
      <StyledContainer>
        <Content>
          {onQuickAddClick && <QuickAddButton onClick={onQuickAddClick} productName={product.name} />}
          <Link href={href} route={route}>
            <a href={href} onClick={handleClick} data-testid={TEST_ID_LIST_VIEW_PRODUCT_CARD}>
              <StyledProductImage product={product} width={160} height={160} />
              <Details>
                <Price product={product} />
                <Name>
                  <Truncate lines={3}>{product.name}</Truncate>
                </Name>
                {brandName && <Brand>{brandName}</Brand>}
                <StrainDetails product={product} />
              </Details>
            </a>
          </Link>
        </Content>
      </StyledContainer>
    );
  }
);

const StyledContainer = styled(Container)`
  ${({ theme }) => css`
    ${theme.breakpoints.down('xs')} {
      padding: 0;
    }
  `}
`;

export const listViewProductCardImageStyles = css`
  ${({ theme }) => theme.breakpoints.down('xs')} {
    width: 100%;
    /* Total horizontal margin space === 87px */
    /* This results in the same value as the width to get a square image */
    height: calc((100vw - 87px) / 2);
  }
`;

const StyledProductImage = styled(ProductImage)`
  ${listViewProductCardImageStyles}
`;
